import React, { useState } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { InputField, TextField } from '@getgo/chameleon-web-react-wrapper';
import { Typography, Button, Chip, IconButton, Select, Option } from '@getgo/chameleon-web-react-wrapper';
import { AddOutlinedIcon, TrashOutlinedIcon } from '@getgo/chameleon-icons/react';
import st from 'core/shared-translations';
import mt from 'core/message-translations';
import classes from './client-details-tab.scss';

const t = defineMessages({
  'client-edit.field.all.scopes.placeholder': {
    id: 'client-edit.field.all.scopes.placeholder',
    defaultMessage: 'Select public scope',
    description: 'Label for scope dropdown',
  },
  'client-edit.field.scopes': {
    id: 'client-edit.field.scopes',
    defaultMessage: 'Or enter scope ID',
    description: 'Label for the Scope ID field',
  },
  'client-edit.field.scopes.placeholder': {
    id: 'client-edit.field.scopes.placeholder',
    defaultMessage: 'Enter scope ID',
    description: 'Placeholder for the Scope ID field',
  },
});

const ClientScopesAdminTab = ({
  client,
  canWrite,
  scopes = [],
  onAddScope,
  onDeleteScope,
  rawScopeList = [],
  scopeValidationErrors,
  clearScopeError
}) => {
  const intl = useIntl();
  const [selectedScope, setSelectedScope] = useState('');
  const [newScope, setNewScope] = useState('');

  const handleAddSelectedScope = () => {
    if (selectedScope.trim() && !(scopes || []).includes(selectedScope.trim())) {
      onAddScope(selectedScope.trim());
      setSelectedScope('');
    }
  };

  const handleAddNewScope = async () => {
    if (newScope.trim() && !(scopes || []).includes(newScope.trim())) {
      const success = await onAddScope(newScope.trim());
      if (success) {
        setNewScope('');
      }
    }
  };

  const handleDeleteScope = (scopeToDelete) => {
    onDeleteScope(scopeToDelete);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewScope(value);
    if (clearScopeError && scopeValidationErrors) {
      clearScopeError();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNewScope();
    }
  };

  return (
    <React.Fragment>
      <Box className={classes["ownersContainer"]}>
        <Box className={classes["scopesTabSpacing"]}>
          <Select
            id="selectedScope"
            name="selectedScope"
            value={selectedScope}
            fullwidth
            inDialog
            size="medium"
            onChange={(e) => setSelectedScope(e.target.value)}
            label={intl.formatMessage(t['client-edit.field.all.scopes.placeholder'])}
            disabled={!canWrite}
          >
            <Option value="">
              <FormattedMessage {...t['client-edit.field.all.scopes.placeholder']} />
            </Option>
            {(rawScopeList || [])
              .filter(scope => !(scopes || []).includes(scope))
              .map((scope, index) => (
                <Option key={index} value={scope}>
                  {scope}
                </Option>
              ))}
          </Select>
          <Button
            variant="secondary"
            onClick={handleAddSelectedScope}
            disabled={!canWrite || !selectedScope || (scopes || []).includes(selectedScope)}
            leadingIcon={<AddOutlinedIcon />}
            className={classes["scopeButton"]}
          >
            <FormattedMessage {...st["shared.add"]} />
          </Button>
        </Box>
        <Box className={classes["scopesTabSpacing"]}>
          <TextField
            id="newScope"
            name="newScope"
            value={newScope}
            fullwidth
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            label={intl.formatMessage(t['client-edit.field.scopes'])}
            placeholder={intl.formatMessage(t['client-edit.field.scopes.placeholder'])}
            disabled={!canWrite}
            error={!!scopeValidationErrors}
            helperText={scopeValidationErrors && intl.formatMessage(mt[scopeValidationErrors])}
          >
            <FormattedMessage {...t['client-edit.field.scopes']} />
          </TextField>
          <Button
            variant="secondary"
            onClick={handleAddNewScope}
            disabled={!canWrite || !newScope.trim() || (scopes || []).includes(newScope.trim()) || !!scopeValidationErrors}
            leadingIcon={<AddOutlinedIcon />}
            className={classes["scopeButton"]}
          >
            <FormattedMessage {...st["shared.add"]} />
          </Button>
        </Box>
        <Box>
          {scopes && scopes.length !== 0 && scopes.map((scope, index) => (
            <Box key={`scope-${index}`}>
              <Typography variant="body-small">
                {scope}
              </Typography>
              <IconButton
                data-testid="deleteScopeButton"
                onClick={() => handleDeleteScope(scope)}>
                <TrashOutlinedIcon color="#2C72DE" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    </React.Fragment>
  );
};

ClientScopesAdminTab.propTypes = {
  client: PropTypes.object.isRequired,
  canWrite: PropTypes.bool,
  scopes: PropTypes.array,
  onAddScope: PropTypes.func.isRequired,
  onDeleteScope: PropTypes.func.isRequired,
  rawScopeList: PropTypes.array,
  scopeValidationErrors: PropTypes.string,
  clearScopeError: PropTypes.func.isRequired,
};

export default ClientScopesAdminTab;
